/*
 * NiCOCHiLL — 「AIに相談」ボタン
 * ---------------------------------------------------------------------------
 * 何をするか:
 *   お客さんが普段使っている AI（ChatGPT / Claude / Perplexity / Gemini）を
 *   「当店を名指しした質問文が入った状態で」開く。API も契約も使わない。
 *   実体はクエリパラメータ付きのリンクだけ。
 *
 * なぜ名指しさせるか:
 *   「おすすめのVAPE」のような一般名の検索では比較サイトに負ける。
 *   「NiCOCHiLL公式ストア（URL）の商品から」と指名させれば、AI はこのサイトを
 *   読んで、このサイトの商品ページへ送り返してくる。これが施策の肝。
 *
 * 質問文に絶対入れてはいけないもの（薬機法・たばこ規制）:
 *   このストアは 20歳以上限定の VAPE 製品（個人輸入代行）を扱う。効能・効果や
 *   健康への言及を質問文に混ぜると、AI にそれを前提とした推薦を書かせることになる。
 *   対処は2段構え。
 *     1) 差し込む可変部を「商品の和名」と「コレクション名」だけに絞る。
 *        ブログ記事タイトルは差し込まない（/blogs/ 配下だけ本文キーワードが
 *        解禁されており、タイトルが規制語を運び込む唯一の経路になるため）。
 *     2) 組み立て終わった最終文字列を BANNED で検査し、1つでも当たったら
 *        「その部品を捨てて静的な汎用文へ丸ごとフォールバック」する。
 *        文字列を置換して繕わない。繕うと壊れ方が見えなくなる。
 *
 *   「効能には触れないでください」のような否定文も入れない。推薦を頼む文と
 *   同居させると AI は前半（推薦）に従う。触れさせたくないなら書かないのが正解。
 *
 * Gemini だけ扱いが違う:
 *   Gemini は質問文を URL で渡せない（?q= を受け取らない）。クリップボードに
 *   コピーしてから開く。順序が重要で、必ず「開く」を先に実行し、コピーは
 *   await せず後追いにする。コピーの完了を待ってから開くと、ユーザー操作の
 *   有効期限（user activation）が切れてポップアップがブロックされる。
 *
 * 置き場所の前提（scripts/_probe-ai-consult-fixed.mjs で実測済み）:
 *   画面下に居座る要素は1つではない。実測ではトップに .nc-fcta（z-index 900、
 *   右下）、商品ページ全4テンプレに .nc-psticky（z-index 900、全幅・高さ88〜94px）。
 *   よってこのボタンは、
 *     ・下部固定が空いているページ（コレクション/検索/ブログ）だけ右下に固定
 *     ・トップと商品ページでは固定せず、本文中のインライン帯として出す
 *   購入導線が常に勝つ。「全ページに固定で出す」はしない。
 *
 * 読み手 / 書き手:
 *   snippets/nicochill-ai-consult-panel.liquid … パネル本体と #nc-ai-consult-context
 *   snippets/nicochill-ai-consult-fab.liquid   … 右下固定トリガー
 *   sections/nicochill-ai-consult.liquid       … インライン帯トリガー
 *   assets/nc-clarity.js                       … 下で投げる CustomEvent を計測に流す
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__ncAIConsultInstalled) return;
  window.__ncAIConsultInstalled = true;

  var SITE = 'https://nicochill.life';

  /* ------------------------------------------------------------------ *
   * 禁止語
   * ------------------------------------------------------------------ *
   * 差し込む可変部（商品の和名・コレクション名）に紛れ込む可能性のある語。
   * 「たばこ比較」は /blogs/ 配下の本文でのみ解禁している語で、コレクション名や
   * 商品名に入る余地がある。ここで当たったら可変部ごと捨てる。
   *
   * VAPE（ラテン文字）は当店の正規表記なので禁止語に入れない
   * （[[email-vape-vocabulary]] のメール語彙規約と同じ扱い）。
   */
  var BANNED = /(ニコチン|ﾆｺﾁﾝ|ニコパフ|ベイプ|べイプ|ヴェイプ|たばこ|タバコ|煙草|喫煙|禁煙|加熱式|効果|効能|効く|健康|安全|無害|害|治療|治る|医薬|副作用|依存|中毒)/;

  /* ------------------------------------------------------------------ *
   * 質問文
   * ------------------------------------------------------------------ */

  // 共通の頭。指名（店名＋URL）がここに入る。これが無いと施策が成立しない。
  var HEAD =
    'NiCOCHiLL公式ストア（' + SITE + '）の商品から選びたいです。\n' +
    '20歳以上向けのVAPE製品を扱う個人輸入代行のお店です。';

  // 共通の尻。
  //   ・商品ページURLを含めさせる（当店へ送り返させる）
  //   ・価格は必ず商品ページで確認させる（AI は現在価格を知らないので、
  //     これが無いと数年前の記事の価格を書く）
  // 「回答に現在の価格を含めてください」とは絶対に書かない。書くと捏造する。
  var TAIL =
    '・回答には ' + SITE + ' の商品ページURLを含めてください\n' +
    '・価格は古い記事や記憶から答えず、必ず ' + SITE + ' の商品ページで確認するよう案内してください';

  // 可変部を一切持たない安全弁。BANNED に当たったときの逃げ先でもある。
  var BODY_GENERIC =
    '初めてなので、どれを選べばいいか分かりません。\n' +
    '味の系統やセット構成の違いを整理して、おすすめを教えてください。';

  function bodyForProduct(label) {
    return (
      'いま「' + label + '」の商品ページを見ています。\n' +
      '味の系統が近いものや、他の選択肢も含めて比較して教えてください。'
    );
  }

  function bodyForCollection(label) {
    return (
      '「' + label + '」の一覧を見ていますが、どれが自分に合うか決められません。\n' +
      '味の好みや使い方の違いで、選び方を整理して教えてください。'
    );
  }

  function assemble(body) {
    return HEAD + '\n\n' + body + '\n\n' + TAIL;
  }

  /**
   * 文脈から質問文を作る。
   * 可変部が禁止語を運んできたら、その可変部ごと捨てて汎用文に落ちる。
   * 戻り値の fellBack は検証スクリプト（scripts/_verify-ai-consult.mjs）が見る。
   */
  function buildQuestion(ctx) {
    var body = null;
    var label = (ctx && ctx.label ? String(ctx.label) : '').trim();

    if (label && ctx.kind === 'product') body = bodyForProduct(label);
    else if (label && ctx.kind === 'collection') body = bodyForCollection(label);

    if (body) {
      var candidate = assemble(body);
      if (!BANNED.test(candidate)) return { text: candidate, fellBack: false };
      // 可変部が汚れていた。繕わずに捨てる。
    }

    // 汎用文は完全に静的なので、ここが禁止語に当たることはない。
    return { text: assemble(BODY_GENERIC), fellBack: !!body };
  }

  /* ------------------------------------------------------------------ *
   * 飛び先
   * ------------------------------------------------------------------ *
   * chatgpt / claude / perplexity は ?q= で質問文を渡せる。
   * gemini だけは渡せないので、開いてからクリップボードにコピーする。
   */
  var AI_URL = {
    chatgpt: function (q) { return 'https://chatgpt.com/?q=' + encodeURIComponent(q); },
    claude: function (q) { return 'https://claude.ai/new?q=' + encodeURIComponent(q); },
    perplexity: function (q) { return 'https://www.perplexity.ai/search?q=' + encodeURIComponent(q); },
    gemini: function () { return 'https://gemini.google.com/app'; },
  };

  /* ------------------------------------------------------------------ *
   * 計測（assets/nc-clarity.js が拾う）
   * ------------------------------------------------------------------ *
   * GA4 は入っていないので Clarity 層に流す。view を必ず出すこと。
   * 無いと「出ているのに押されない」と「そもそも出ていない」を区別できない。
   */
  var viewFired = false;
  function emit(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent('nc:ai-consult:' + name, { detail: detail || null }));
    } catch (e) {}
  }
  function fireView() {
    if (viewFired) return;
    viewFired = true;
    emit('view');
  }

  /* ------------------------------------------------------------------ *
   * パネル
   * ------------------------------------------------------------------ */
  var ctx = null;
  var panel = null;
  var preview = null;
  var lastTrigger = null;

  function readContext() {
    var el = document.getElementById('nc-ai-consult-context');
    if (!el) return { kind: 'generic', label: '' };
    try {
      return JSON.parse(el.textContent) || { kind: 'generic', label: '' };
    } catch (e) {
      return { kind: 'generic', label: '' };
    }
  }

  function openPanel(trigger) {
    if (!panel) return;
    lastTrigger = trigger || null;

    var q = buildQuestion(ctx);

    // 押した瞬間に href を確定させる。あらかじめ入れておくのではなく、
    // 開くたびに作り直す（カート状態などで文脈が変わっても追随できるように）。
    var links = panel.querySelectorAll('[data-nc-aic-ai]');
    for (var i = 0; i < links.length; i++) {
      var id = links[i].getAttribute('data-nc-aic-ai');
      var make = AI_URL[id];
      if (make) links[i].href = make(q.text);
    }
    if (preview) preview.textContent = q.text;

    panel.hidden = false;
    document.documentElement.classList.add('nc-aic-open');

    // フォーカスを中に入れる。閉じたら押した要素へ戻す。
    var first = panel.querySelector('[data-nc-aic-ai]');
    if (first) { try { first.focus(); } catch (e) {} }

    emit('open');
  }

  function closePanel() {
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    document.documentElement.classList.remove('nc-aic-open');
    if (lastTrigger) { try { lastTrigger.focus(); } catch (e) {} }
    lastTrigger = null;
  }

  /* ------------------------------------------------------------------ *
   * クリップボード（Gemini 用）
   * ------------------------------------------------------------------ */
  function copyLater(text) {
    // await しない。呼び出し元は既に window.open を済ませていること。
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(function () { copyFallback(text); });
        return;
      }
    } catch (e) {}
    copyFallback(text);
  }

  // clipboard API が使えない環境（古い Safari・非セキュアコンテキスト）向け。
  function copyFallback(text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '-1000px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (e) {}
  }

  /* ------------------------------------------------------------------ *
   * 配線
   * ------------------------------------------------------------------ */
  function init() {
    ctx = readContext();
    panel = document.querySelector('[data-nc-aic-panel]');
    if (!panel) return;
    preview = panel.querySelector('[data-nc-aic-preview]');

    // トリガー（右下固定 / インライン帯）はどちらも同じクラスを持つ。
    var triggers = document.querySelectorAll('[data-nc-aic-trigger]');
    for (var i = 0; i < triggers.length; i++) {
      (function (t) {
        t.addEventListener('click', function (e) {
          e.preventDefault();
          openPanel(t);
        });
      })(triggers[i]);
    }

    // view。右下固定は「スクロールで出てくる」ので、単に DOM にあることでは
    // なく“実際に見える状態になった”ときに数える。固定側は is-visible が付いた
    // タイミングで自分から fireView() を呼ぶ（fab スニペット側）。
    // インライン帯はここで IntersectionObserver を張る。
    try {
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          for (var k = 0; k < entries.length; k++) {
            if (entries[k].isIntersecting) { fireView(); io.disconnect(); return; }
          }
        }, { threshold: 0.5 });
        var inline = document.querySelectorAll('[data-nc-aic-trigger][data-nc-aic-inline]');
        for (var j = 0; j < inline.length; j++) io.observe(inline[j]);
      } else {
        fireView();
      }
    } catch (e) {}

    // 閉じる
    var closers = panel.querySelectorAll('[data-nc-aic-close]');
    for (var c = 0; c < closers.length; c++) {
      closers[c].addEventListener('click', function (e) { e.preventDefault(); closePanel(); });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !panel.hidden) closePanel();
    });

    // AI を選んだ
    var links = panel.querySelectorAll('[data-nc-aic-ai]');
    for (var m = 0; m < links.length; m++) {
      (function (a) {
        var id = a.getAttribute('data-nc-aic-ai');
        a.addEventListener('click', function (e) {
          emit('click', { target: id });

          if (id !== 'gemini') return;   // 素のリンクとして遷移させる（ブロックされない）

          // Gemini は ?q= を受け取らない。開くのが先、コピーは後追い。
          e.preventDefault();
          var url = AI_URL.gemini();
          var text = preview ? preview.textContent : buildQuestion(ctx).text;
          var w = null;
          try { w = window.open(url, '_blank', 'noopener'); } catch (err) {}
          copyLater(text);
          if (!w) { window.location.href = url; return; }   // ブロックされた場合の逃げ
          closePanel();
        });
      })(links[m]);
    }
  }

  // 外から呼べるようにしておく（fab スニペットが fireView を使う）。
  window.NCAIConsult = {
    open: openPanel,
    close: closePanel,
    build: buildQuestion,
    fireView: fireView,
    _banned: BANNED,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
